// @givmo/fundraisers-module — an invitation to join another campaign as one of its layers, the child's side
// (FCM-BE34; frames 10 and 10b). Accepting is the organization's consent (Q3), so the card says, before Accept,
// the six things accepting means (plan §FC-16.2, revision 2's terms). Accept and decline are the console's
// doors only, never a tool. When the campaign can't fit, Accept waits and the card gives the server's reason.
import { useMutation, useQuery } from '@tanstack/react-query';
import { sdk, type P2pLayerInviteOut } from '@givmo/api-client';
import { Button, Card, CardBody, toast } from '@givmo/design-system';
import { CircleAlert } from 'lucide-react';
import { PHONE_HIT, StateNotice } from './lifecycle-ui';
import { displayP2pDate } from './p2p-decode';
import { LAYERS_COPY as C } from './layers-copy';
import { layerRefusal } from './layers-decode';
import { publicFundraiserOptions } from './layers-queries';
import { useRecipientNames } from './recipient-names';
import { useLivePage } from './use-live-page';

export function LayerInvitation({
  invite,
  campaignId,
  canAnswer,
  onAnswered,
}: {
  invite: P2pLayerInviteOut;
  campaignId: string;
  /** The moderation rank on this campaign: the one that may accept or decline. */
  canAnswer: boolean;
  onAnswered: () => Promise<void>;
}) {
  const live = useLivePage();
  const parent = invite.campaign_name ?? C.anotherCampaign;
  // "From {organization}": the inviting campaign's own byline, off its public page, when it has one.
  const organization = useQuery({ ...publicFundraiserOptions(invite.campaign_id), retry: false });
  // What the inviting campaign requires (revision 2's P2): its charities when it picks them, or the list
  // that binds its pages and layers when they pick.
  const requires = invite.requires;
  const kind = requires.recipient_choice === 'supporters' ? 'list' : 'campaign';
  const required = useRecipientNames(kind === 'list' ? requires.binding_list : requires);
  const answer = useMutation({
    mutationFn: async (act: 'accept' | 'decline') => {
      const path = { fundraiser_id: campaignId, invite_id: invite.id };
      if (act === 'accept')
        await sdk.campaignsAcceptCampaignLayerInvite({ path, throwOnError: true });
      else await sdk.campaignsDeclineCampaignLayerInvite({ path, throwOnError: true });
    },
    onSuccess: async (_data, act) => {
      // The answered invitation leaves the tab's list with the re-read, and this card goes with it, so
      // the confirmation comes first, as the refusal's does (FCM-WEB61).
      if (live()) toast.success(act === 'accept' ? C.accepted : C.declined);
      await onAnswered();
    },
    // A refused accept leaves the invitation pending: the server's words, and the page re-reads.
    onError: (error) => {
      if (!live()) return;
      toast.error(layerRefusal(error).message);
      void onAnswered();
    },
  });
  const terms = C.inviteTerms(
    parent,
    required.ready && (kind === 'campaign' || requires.binding_list != null)
      ? { kind, charities: required.items.map((item) => item.name) }
      : null,
  );
  const expires = displayP2pDate(invite.expires_at);
  return (
    <Card flush testId={`layers-invitation-${invite.id}`}>
      {/* The parent's name is the point, so the title wraps rather than truncating as a CardHeader's does. */}
      <div className="border-b border-border-subtle px-20 py-16">
        <h3 className="break-words font-sans text-s16 font-w600 tracking-givmo text-text-strong">
          {C.inviteTitle(parent)}
        </h3>
        <p className="mt-2 font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
          {organization.data ? C.inviteFrom(organization.data, expires) : `Expires ${expires}`}
        </p>
      </div>
      <CardBody className="flex flex-col gap-12">
        <p className="text-s14 font-w600 text-text-strong">{C.inviteIf}</p>
        <ul
          className="flex list-disc flex-col gap-8 pl-20 text-s14 leading-h15 text-text-secondary"
          data-testid="layers-invitation-terms"
        >
          {terms.map((term) => (
            <li key={term}>{term}</li>
          ))}
        </ul>
        <p className="text-s14 font-w600 text-text-strong" data-testid="layers-invitation-consent">
          {C.inviteConsent}
        </p>
        {!invite.can_accept && invite.cannot_accept_reason && (
          <StateNotice icon={CircleAlert} testId="layers-invitation-no-fit" action={null}>
            {invite.cannot_accept_reason}
          </StateNotice>
        )}
        {canAnswer && (
          <div className="mt-4 flex flex-wrap justify-end gap-8">
            <Button
              appearance="saas"
              tone="secondary"
              className={PHONE_HIT}
              disabled={answer.isPending}
              loading={answer.isPending && answer.variables === 'decline'}
              onClick={() => answer.mutate('decline')}
              testId="layers-invitation-decline"
            >
              {C.decline}
            </Button>
            <Button
              appearance="saas"
              className={PHONE_HIT}
              loading={answer.isPending && answer.variables === 'accept'}
              // Consent fails closed: Accept waits until every term it consents to is on the card
              // (term 5 names the charities only once their names are read).
              disabled={answer.isPending || !invite.can_accept || !required.ready}
              onClick={() => answer.mutate('accept')}
              testId="layers-invitation-accept"
            >
              {C.accept}
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
